import api from '../api';
import { normalizeError } from './auditFindingsService';

const API_BASE = '/audit/form-evidence';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ['image/', 'application/pdf', 'video/'];

/**
 * Validate a File object before upload.
 * @param {File} file
 * @returns {{ valid: boolean, message?: string }}
 */
export const validateEvidenceFile = (file) => {
  if (!file) return { valid: false, message: 'No file selected.' };
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, message: `File exceeds 10 MB limit (${(file.size / 1024 / 1024).toFixed(1)} MB).` };
  }
  const allowed = ALLOWED_TYPES.some((type) => file.type.startsWith(type));
  if (!allowed) {
    return { valid: false, message: `File type ${file.type} is not allowed. Allowed: images, PDF, video.` };
  }
  return { valid: true };
};

/**
 * Upload a single evidence file.
 * @param {{ file: File, projectId: number, formId?: number, processId?: number, scopeKey: string, documentCode: string, evidenceType: string, metadata?: object }} data
 * @returns {Promise<object>}
 */
export const uploadEvidence = async ({
  file,
  projectId,
  formId,
  processId,
  scopeKey,
  documentCode,
  evidenceType,
  metadata,
}) => {
  const validation = validateEvidenceFile(file);
  if (!validation.valid) {
    return normalizeError({ message: validation.message }, 'Upload evidence');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('projectId', String(projectId));
  if (formId != null) formData.append('formId', String(formId));
  if (processId != null) formData.append('processId', String(processId));
  formData.append('scopeKey', scopeKey);
  formData.append('documentCode', documentCode);
  formData.append('evidenceType', evidenceType);
  if (metadata) formData.append('metadata', JSON.stringify(metadata));

  try {
    const res = await api.post(`${API_BASE}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (err) {
    return normalizeError(err, 'Upload evidence');
  }
};

/**
 * List evidence for a project.
 * @param {number} projectId
 * @returns {Promise<array>}
 */
export const getEvidenceByProject = (projectId) =>
  api.get(`${API_BASE}/project/${projectId}`).then((r) => r.data).catch((err) => normalizeError(err, 'Get evidence by project'));

/**
 * List evidence scoped to a project + scope + document code.
 * @param {number} projectId
 * @param {string} scopeKey
 * @param {string} documentCode
 * @returns {Promise<array>}
 */
export const getEvidenceByProjectAndScope = (projectId, scopeKey, documentCode) =>
  api.get(`${API_BASE}/project/${projectId}/scope/${scopeKey}/doc/${documentCode}`).then((r) => r.data).catch((err) => normalizeError(err, 'Get evidence by scope'));

/**
 * List evidence for a form.
 * @param {number} formId
 * @returns {Promise<array>}
 */
export const getEvidenceByFormId = (formId) =>
  api.get(`${API_BASE}/form/${formId}`).then((r) => r.data).catch((err) => normalizeError(err, 'Get evidence by form'));

/**
 * Soft-delete an evidence record.
 * @param {number} id
 * @returns {Promise<object>}
 */
export const deleteEvidence = (id) =>
  api.delete(`${API_BASE}/${id}`).then((r) => r.data).catch((err) => normalizeError(err, 'Delete evidence'));

/** Download an evidence file using the authenticated API client. */
export const downloadEvidence = async (id, fileName = 'audit-evidence') => {
  try {
    const response = await api.get(`${API_BASE}/${id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { success: true };
  } catch (err) {
    return normalizeError(err, 'Download evidence');
  }
};

/**
 * Count evidence records for a project.
 * @param {number} projectId
 * @returns {Promise<number>}
 */
export const countEvidenceByProject = (projectId) =>
  api.get(`${API_BASE}/project/${projectId}/count`).then((r) => r.data).catch((err) => normalizeError(err, 'Count evidence'));
